/**
 * Catalogue display resolvers — turn a stable code into its live
 * display string, read at render time from the canonical hierarchy
 * collections per ADR-0007. If the code is missing or the hierarchy
 * doc has been tombstoned, render an explicit `(unknown ...)` string
 * rather than a stale embedded snapshot.
 *
 * OMON-079 79c (2026-05-10): snapshot fallbacks removed. Pre-OMON-079
 * goal docs carrying a `display` field on their category coding no
 * longer flow through here — those collections were wiped + reseeded
 * with no embedded displays.
 */

import { state } from "../state";
import type { ObservationDefinition } from "../types";
import { resolveCategoryCodes } from "./category";

export function resolveCategoryDisplay(code: string): string {
  if (!code) return "(unknown Goal Category)";
  return state.goalCategories.get(code)?.display
    || `(unknown Goal Category: ${code})`;
}

export function resolveAimDisplay(categoryCode: string, subcategoryCode: string): string {
  if (!categoryCode || !subcategoryCode) return "(unknown Goal Sub-category)";
  return state.goalSubcategories.get(`${categoryCode}__${subcategoryCode}`)?.display
    || `(unknown Goal Sub-category: ${subcategoryCode})`;
}

export function resolveODHierarchy(od: ObservationDefinition): {
  categoryCode: string;
  categoryDisplay: string;
  subcategoryCode: string;
  subcategoryDisplay: string;
} {
  // OMON-166: resolve by `coding[].system`, not array index. After the v3
  // curation, capability codings sit at arbitrary positions — the old
  // positional read (`category[1]`/`category[2]`) mistook a capability coding
  // for the sub-category (e.g. "developmental"), which then tripped the
  // "sub-category not found" save guard. resolveCategoryCodes matches by system.
  const resolved = resolveCategoryCodes(od.category);
  const categoryCode = resolved.goalCategory || "";
  const subcategoryCode = resolved.goalSubcategory || "";
  return {
    categoryCode,
    categoryDisplay: resolveCategoryDisplay(categoryCode),
    subcategoryCode,
    subcategoryDisplay: resolveAimDisplay(categoryCode, subcategoryCode),
  };
}
